import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../hooks/useEvents';
import { useParticipants } from '../hooks/useParticipants';
import { fetchAllFreeGames } from '../services/deals';

import HomeHeader from './home/HomeHeader';
import NextEventCard from './home/NextEventCard';
import NewsSection from './home/NewsSection';
import EventDialogs from './events/EventDialogs';
import LocationPopUpDialog from '../components/LocationPopUpDialog';
import EventDebriefDialog from './events/EventDebriefDialog';

import DealsCarousel from '../components/DealsCarousel';

import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { user, openLogin } = useAuth();
  const {
    pastEvents,
    upcomingEvents,
    loading,
    selectedEvent,
    setSelectedEvent,
    openDialog,
    setOpenDialog,
    handleRegisterEvent
  } = useEvents();

  const {
    participants,
    loadingParticipants,
    participantsDialogOpen,
    setParticipantsDialogOpen,
    handleParticipantsClick
  } = useParticipants();

  const [familyMembers, setFamilyMembers] = useState<any[]>([]);

  // Location Dialog State
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);

  // Debrief Dialog State
  const [selectedPastEventId, setSelectedPastEventId] = useState<string | null>(null);
  const [openDebriefDialog, setOpenDebriefDialog] = useState(false);

  // Charger les membres de la famille
  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'users'), where('parentId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const members = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFamilyMembers(members);
      });
      return () => unsubscribe();
    } else {
      setFamilyMembers([]);
    }
  }, [user]);

  const handleSplashClick = React.useCallback(() => {
    if (!user) {
      alert("Vous devez être connecté pour voir les autres événements.");
      return;
    }
    navigate('/events');
  }, [user, navigate]);

  const handleNextEventRegister = React.useCallback(() => {
    if (!user) {
      openLogin();
      return;
    }
    setSelectedEvent(upcomingEvents[0]);
    setOpenDialog(true);
  }, [user, upcomingEvents, openLogin, setSelectedEvent, setOpenDialog]);

  /*@ts-ignore*/
  const handleNextEventParticipantsClick = React.useCallback((e: React.MouseEvent) => {
    if (upcomingEvents.length > 0) {
      handleParticipantsClick(e, upcomingEvents[0]);
    }
  }, [upcomingEvents, handleParticipantsClick]);

  const handleLocationClick = React.useCallback((locationId: string) => {
    setSelectedLocationId(locationId);
    setIsLocationDialogOpen(true);
  }, []);

  const handlePastEventClick = React.useCallback((eventId: string) => {
    setSelectedPastEventId(eventId);
    setOpenDebriefDialog(true);
  }, []);

  // Calcul du statut d'inscription Famille pour le prochain événement
  let customStatusText = undefined;
  let customStatusColor = undefined;

  if (user && upcomingEvents.length > 0 && familyMembers.length > 0) {
    const nextEvent = upcomingEvents[0];
    const eventParticipants = nextEvent.participants || [];

    // Le user est-il inscrit ?
    const isUserRegistered = eventParticipants.includes(user.uid);

    // Combien d'enfants sont inscrits ?
    /*@ts-ignore*/
    const registeredChildrenCount = familyMembers.filter(member => eventParticipants.includes(member.id)).length;

    const totalMembers = 1 + familyMembers.length; // User + Enfants
    const totalRegistered = (isUserRegistered ? 1 : 0) + registeredChildrenCount;

    if (totalRegistered === 0) {
      customStatusText = `NON INSCRIT (0/${totalMembers})`;
      customStatusColor = 'error.main';
    } else if (totalRegistered === totalMembers) {
      customStatusText = `INSCRITS (${totalRegistered}/${totalMembers})`; // Pluriel car totalMembers >= 2 ici
      customStatusColor = 'success.main';
    } else {
      // Partiellement inscrits
      const plural = totalRegistered > 1 ? 'S' : '';
      customStatusText = `INSCRIT${plural} (${totalRegistered}/${totalMembers})`;
      customStatusColor = 'warning.main'; // Orange
    }
  }

  return (
    <PageLayout containerWidth={false}>
      <HomeHeader />

      <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: '1800px', mx: 'auto' }}>
        <Grid container spacing={4}>

          {/* LEFT COLUMN: Next Event + News */}
          <Grid item xs={12} md={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

              {/* Prochain événement - HERO SECTION */}
              {upcomingEvents.length > 0 && (
                <Box>
                  <NextEventCard
                    event={upcomingEvents[0]}
                    user={user}
                    onRegister={handleNextEventRegister}
                    onParticipantsClick={handleNextEventParticipantsClick}
                    additionalEventsCount={Math.max(0, upcomingEvents.length - 1)}
                    onSplashClick={handleSplashClick}
                    /*@ts-ignore*/
                    customStatusText={customStatusText}
                    /*@ts-ignore*/
                    customStatusColor={customStatusColor}
                    onLocationClick={handleLocationClick}
                  />
                </Box>
              )}

              {/* Actualités & Événements passés */}
              <NewsSection
                user={user}
                pastEvents={pastEvents}
                onPastEventClick={handlePastEventClick}
              />
            </Box>
          </Grid>

          {/* RIGHT COLUMN: Deals Carousel (Desktop only) */}
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 100, height: 'calc(100vh - 120px)' }}>
              <DealsCarousel />
            </Box>
          </Grid>

        </Grid>
      </Box>

      <EventDialogs
        openRegisterDialog={openDialog}
        setOpenRegisterDialog={setOpenDialog}
        selectedEvent={selectedEvent}
        user={user}
        onRegisterEvent={(idsToAdd, idsToRemove) => handleRegisterEvent(idsToAdd, idsToRemove)}

        openParticipantsList={participantsDialogOpen}
        setOpenParticipantsList={setParticipantsDialogOpen}
        participants={participants}

        // Props requises par le composant partagé mais non utilisées sur la Home pour l'instant
        openCreateDialog={false}
        setOpenCreateDialog={() => { }}
        onEventSaved={() => { }}
        openNonMemberDialog={false}
        setOpenNonMemberDialog={() => { }}
      />

      <LocationPopUpDialog
        open={isLocationDialogOpen}
        onClose={() => setIsLocationDialogOpen(false)}
        locationId={selectedLocationId}
      />

      <EventDebriefDialog
        open={openDebriefDialog}
        onClose={() => setOpenDebriefDialog(false)}
        eventId={selectedPastEventId}
        onEventUpdated={() => { }}
        onEdit={() => navigate('/events')}
        onDelete={() => navigate('/events')}
        onLocationClick={handleLocationClick}
      />
    </PageLayout>
  );
};

export default Home;
